import { useState, useEffect } from 'react';
import {
  Bell,
  Plus,
  Calendar,
  Eye,
  Edit,
  Trash2,
  Send,
  Clock,
  CheckCircle,
  X,
  Megaphone
} from 'lucide-react';
import { adminAnnouncementsApi } from '../utils/apiService';
import { toast } from 'sonner';

interface Announcement {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'success' | 'update';
  status: 'draft' | 'scheduled' | 'published';
  scheduledDate?: string;
  publishedDate?: string;
  views: number;
}

export default function Announcements() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch announcements from API
  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        setIsLoading(true);
        const result = await adminAnnouncementsApi.getAll();
        
        // Map backend data to frontend format
        const mappedAnnouncements = result.announcements.map((ann: any) => ({
          id: ann._id,
          title: ann.title,
          message: ann.message,
          type: ann.type || 'info',
          status: ann.status || 'draft',
          scheduledDate: ann.scheduledDate
            ? new Date(ann.scheduledDate).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
            : undefined,
          publishedDate: ann.publishedDate ? new Date(ann.publishedDate).toISOString().split('T')[0] : undefined,
          views: ann.views || 0
        }));

        setAnnouncements(mappedAnnouncements);
      } catch (error) {
        console.error('Failed to fetch announcements:', error);
        toast.error('Failed to load announcements');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnnouncements();
  }, []);

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this announcement?')) {
      try {
        await adminAnnouncementsApi.delete(id);
        setAnnouncements(prev => prev.filter(a => a.id !== id));
        toast.success('Announcement deleted successfully');
      } catch (error) {
        console.error('Failed to delete announcement:', error);
        toast.error('Failed to delete announcement');
      }
    }
  };

  const stats = {
    total: announcements.length,
    published: announcements.filter(a => a.status === 'published').length,
    scheduled: announcements.filter(a => a.status === 'scheduled').length,
    draft: announcements.filter(a => a.status === 'draft').length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-800 mb-2">Announcements</h2>
          <p className="text-slate-600">Create and manage app-wide announcements</p>
        </div>
        <button
          onClick={() => {
            setEditingAnnouncement(null);
            setShowCreateModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#2B7A7E] text-white rounded-xl hover:bg-[#236266] transition-colors"
        >
          <Plus className="w-5 h-5" />
          New Announcement
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Total</p>
              <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
            </div>
            <Bell className="w-10 h-10 text-slate-400" />
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 border border-green-200 bg-green-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-700">Published</p>
              <p className="text-2xl font-bold text-green-800">{stats.published}</p>
            </div>
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 border border-blue-200 bg-blue-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-700">Scheduled</p>
              <p className="text-2xl font-bold text-blue-800">{stats.scheduled}</p>
            </div>
            <Clock className="w-10 h-10 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Draft</p>
              <p className="text-2xl font-bold text-slate-800">{stats.draft}</p>
            </div>
            <Edit className="w-10 h-10 text-slate-400" />
          </div>
        </div>
      </div>

      {/* Announcements List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="bg-white rounded-xl p-12 border border-slate-200 text-center">
            <Bell className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-800 mb-2">Loading announcements...</h3>
            <p className="text-slate-600 mb-6">Please wait while we load the announcements</p>
          </div>
        ) : announcements.length === 0 ? (
          <div className="bg-white rounded-xl p-12 border border-slate-200 text-center">
            <Bell className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-800 mb-2">No announcements yet</h3>
            <p className="text-slate-600 mb-6">Create your first announcement to communicate with users</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#2B7A7E] text-white rounded-xl hover:bg-[#236266] transition-colors"
            >
              <Plus className="w-5 h-5" />
              Create Announcement
            </button>
          </div>
        ) : (
          announcements.map(announcement => (
            <div
              key={announcement.id}
              className="bg-white rounded-xl p-6 border-2 border-slate-200 hover:border-[#2B7A7E] transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {/* Type Badge */}
                  <div className={`p-2 rounded-xl ${
                    announcement.type === 'success' ? 'bg-green-100' :
                    announcement.type === 'warning' ? 'bg-amber-100' :
                    announcement.type === 'update' ? 'bg-blue-100' :
                    'bg-slate-100'
                  }`}>
                    <Megaphone className={`w-5 h-5 ${
                      announcement.type === 'success' ? 'text-green-600' :
                      announcement.type === 'warning' ? 'text-amber-600' :
                      announcement.type === 'update' ? 'text-blue-600' :
                      'text-slate-600'
                    }`} />
                  </div>

                  {/* Status Badge */}
                  {announcement.status === 'published' && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm font-semibold">
                      <CheckCircle className="w-4 h-4" />
                      Published
                    </span>
                  )}
                  {announcement.status === 'scheduled' && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold">
                      <Clock className="w-4 h-4" />
                      Scheduled
                    </span>
                  )}
                  {announcement.status === 'draft' && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-sm font-semibold">
                      <Edit className="w-4 h-4" />
                      Draft
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setEditingAnnouncement(announcement);
                      setShowCreateModal(true);
                    }}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Edit className="w-5 h-5 text-slate-600" />
                  </button>
                  <button
                    onClick={() => handleDelete(announcement.id)}
                    className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-5 h-5 text-red-600" />
                  </button>
                </div>
              </div>

              <h3 className="text-xl font-bold text-slate-800 mb-2">{announcement.title}</h3>
              <p className="text-slate-600 mb-4">{announcement.message}</p>

              <div className="flex items-center gap-6 text-sm text-slate-600">
                {announcement.publishedDate && (
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" />
                    <span>Published: {announcement.publishedDate}</span>
                  </div>
                )}
                {announcement.scheduledDate && (
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4" />
                    <span>Scheduled for: {announcement.scheduledDate}</span>
                  </div>
                )}
                {announcement.status === 'published' && (
                  <div className="flex items-center gap-1.5">
                    <Eye className="w-4 h-4" />
                    <span>{announcement.views.toLocaleString()} views</span>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <CreateAnnouncementModal
          announcement={editingAnnouncement}
          onClose={() => {
            setShowCreateModal(false);
            setEditingAnnouncement(null);
          }}
          onSave={(data) => {
            if (editingAnnouncement) {
              setAnnouncements(prev => prev.map(a =>
                a.id === editingAnnouncement.id ? { ...a, ...data } : a
              ));
            } else {
              setAnnouncements(prev => [...prev, {
                id: Date.now().toString(),
                ...data,
                views: 0
              }]);
            }
            setShowCreateModal(false);
            setEditingAnnouncement(null);
          }}
        />
      )}
    </div>
  );
}

// Create/Edit Modal Component
interface CreateAnnouncementModalProps {
  announcement: Announcement | null;
  onClose: () => void;
  onSave: (data: Omit<Announcement, 'id' | 'views'>) => void;
}

function CreateAnnouncementModal({ announcement, onClose, onSave }: CreateAnnouncementModalProps) {
  // Pre-fill date/time from existing scheduledDate if editing
  const existingScheduled = announcement?.scheduledDate
    ? new Date(announcement.scheduledDate)
    : null;

  const [formData, setFormData] = useState({
    title: announcement?.title || '',
    message: announcement?.message || '',
    type: announcement?.type || 'info' as 'info' | 'warning' | 'success' | 'update',
    status: announcement?.status || 'draft' as 'draft' | 'scheduled' | 'published',
    scheduledDate: existingScheduled
      ? existingScheduled.toISOString().split('T')[0]
      : '',
    scheduledTime: existingScheduled
      ? existingScheduled.toTimeString().slice(0, 5)
      : '09:00',
    publishedDate: announcement?.publishedDate || ''
  });
  const [_isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const scheduledISO = formData.scheduledDate
        ? new Date(`${formData.scheduledDate}T${formData.scheduledTime || '09:00'}:00`).toISOString()
        : undefined;

      const payload = {
        title: formData.title,
        message: formData.message,
        type: formData.type,
        status: formData.status,
        ...(scheduledISO && { scheduledDate: scheduledISO })
      };

      if (announcement) {
        // Update existing
        await adminAnnouncementsApi.update(announcement.id, payload);
        toast.success('Announcement updated successfully');
      } else {
        // Create new
        await adminAnnouncementsApi.create(payload);
        toast.success('Announcement created successfully');
      }

      onSave(formData);
      // Refresh page to get updated data
      window.location.reload();
    } catch (error) {
      console.error('Failed to save announcement:', error);
      toast.error('Failed to save announcement');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <h3 className="text-2xl font-bold text-slate-800">
            {announcement ? 'Edit' : 'Create'} Announcement
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-slate-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-[#2B7A7E]"
              placeholder="Enter announcement title"
              required
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Message *
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              rows={4}
              className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-[#2B7A7E] resize-none"
              placeholder="Enter announcement message"
              required
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Type
            </label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
              className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-[#2B7A7E] cursor-pointer"
            >
              <option value="info">Information</option>
              <option value="success">Success</option>
              <option value="warning">Warning</option>
              <option value="update">Update</option>
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Status
            </label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
              className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-[#2B7A7E] cursor-pointer"
            >
              <option value="draft">Save as Draft</option>
              <option value="scheduled">Schedule for Later</option>
              <option value="published">Publish Now</option>
            </select>
          </div>

          {/* Scheduled Date + Time (conditional) */}
          {formData.status === 'scheduled' && (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Schedule Date & Time *
              </label>
              <div className="flex gap-3">
                <input
                  type="date"
                  value={formData.scheduledDate}
                  onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
                  className="flex-1 px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-[#2B7A7E]"
                  required={formData.status === 'scheduled'}
                  min={new Date().toISOString().split('T')[0]}
                />
                <input
                  type="time"
                  value={formData.scheduledTime}
                  onChange={(e) => setFormData({ ...formData, scheduledTime: e.target.value })}
                  className="w-36 px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-[#2B7A7E]"
                  required={formData.status === 'scheduled'}
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">Notification will be sent at this exact date and time.</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border-2 border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#2B7A7E] text-white rounded-xl hover:bg-[#236266] transition-colors"
            >
              <Send className="w-5 h-5" />
              {announcement ? 'Update' : 'Create'} Announcement
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
